import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import ActivityListResource from 'App/resources/ActivityList';
import ActivityAttributes from 'App/babel/attributes/ActivityAttributes';
import ComparisonTypes from 'Resources/ComparisonTypes';
import { getTimelineRow } from 'Components/Helpers/getTimelineRow';
import HistoryLog from '../HistoryLog';
import T, { useTranslation } from 'Components/Helpers/translate';
import React, { useMemo } from 'react';
import { TimelineProps } from 'Components/TimelineRow/TimelineGeneric';
import usePartialLoader, { FetcherFunction } from 'App/components/hooks/usePartialLoader';
import Client from 'App/resources/Model/Client';
import Contact from 'App/resources/Model/Contact';
import TimelineLoadMore from 'Components/TimelineRow/TimelineLoadMore/TimelineLoadMore';
import Event from 'App/resources/Model/Event';
import { Icon } from '@upsales/components';
import { useSoftDeployAccess } from 'App/components/hooks';
import { withMonthHeaders } from '../HistoryLogMonthHeader';

type Props = {
	client: Client;
	// When set, the open-activities query is scoped to the contact instead of the client
	contact?: Contact;
	isContactVariant?: boolean;
	cardAdditionalProps?: Partial<TimelineProps>;
};

const listeners = [
	'activity.added',
	'activity.deleted',
	'activity.updated',
	'appointment.added',
	'appointment.deleted',
	'appointment.updated'
];

const fetcher: FetcherFunction<{ client: Props['client']; contact?: Props['contact'] }, Event> = (
	filters,
	{ client, contact }
) => {
	const EventService = getAngularModule('EventService');

	filters.addSort(ActivityAttributes.date, false);
	// Same-day entries otherwise come back in whatever order the index hands them
	// over, so two rows could swap places between refetches. Angular got a stable
	// order for free: it sorted the page in JS with ActivityDateSorter, and a stable
	// sort left same-date entries in the order the API returned them.
	filters.addSort(ActivityAttributes.id, false);
	if (contact) {
		filters.addFilter({ field: 'contact.id' }, ComparisonTypes.Equals, contact.id);
	} else {
		filters.addFilter(ActivityAttributes.client.attr.id, ComparisonTypes.Equals, client!.id);
	}
	filters.addFilter(ActivityAttributes.projectPlan.attr.id, ComparisonTypes.Equals, null);

	const orBuilder = filters.orBuilder();
	orBuilder.next();
	orBuilder.addFilter(ActivityAttributes.closeDate, ComparisonTypes.Equals, null);
	orBuilder.addFilter(ActivityAttributes.isAppointment, ComparisonTypes.Equals, false);
	orBuilder.next();
	orBuilder.addFilter(ActivityAttributes.isAppointment, ComparisonTypes.Equals, true);
	orBuilder.addFilter({ field: 'outcome' }, ComparisonTypes.Equals, 'planned');
	orBuilder.addFilter(ActivityAttributes.date, ComparisonTypes.GreaterThanEquals, new Date());
	orBuilder.done();

	return ActivityListResource.find(filters.build()).then(res => {
		const activityEvents = res.data.map(activity => {
			const event = EventService.create.Activity(activity);
			event.date = activity.date;
			event.client = client;
			return event;
		});

		return {
			...res,
			data: activityEvents
		};
	});
};

const OpenActivityHistoryLog = ({ client, contact, isContactVariant = false, ...props }: Props) => {
	const {
		data: events,
		total: totalEvents,
		loading,
		loadMore
	} = usePartialLoader({ fetcher, broadcastTypes: listeners, fetcherProps: { client, contact } });
	const { t } = useTranslation();
	const hasRemovedActivities = useSoftDeployAccess('REMOVE_ACTIVITIES');

	const eventComponents = useMemo(
		() =>
			withMonthHeaders(events, (e, index) => (
				<div key={index}>
					{getTimelineRow(e, isContactVariant ? 'Contact' : 'Client', undefined, undefined, undefined, {
						hasAvatar: true
					})}
				</div>
			)),
		[events]
	);

	if (!loading && totalEvents === 0) {
		if (client.active) {
			return (
				<HistoryLog
					title={
						<>
							<Icon name="exclamation-circle" space="prm" />
							{t(hasRemovedActivities ? 'search.accountHasNoActivity' : 'search.accountHasNoTask')}
						</>
					}
					hasPriorHistory
				/>
			);
		}

		return null;
	}

	return (
		<HistoryLog
			title={`${T('default.openActivities')} (${totalEvents})`}
			loading={loading}
			hasPriorHistory={true}
			dashedLine
			selectButtons={null}
			{...props}
		>
			{eventComponents}
			{events.length < totalEvents ? (
				<TimelineLoadMore
					newEventsLoading={loading && events.length > 0}
					numEventsLoaded={events.length}
					totalEvents={totalEvents ? totalEvents : null}
					incrementOffset={loadMore}
				/>
			) : null}
		</HistoryLog>
	);
};

export default OpenActivityHistoryLog;
